import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { Plus, Sparkles, Scale, Loader2, CheckCircle2, ChevronDown, Users, Trash2, RotateCw } from "lucide-react";
import Modal, { Field, Input, Textarea, Select, GoldButton, GhostButton, PageHeader, EmptyState, StatusBadge } from "@/components/Ui";
import { fmtDate, fmtDateTime } from "@/lib/time";
import { caseDisplayNumber } from "@/lib/case";
import FilterPill from "@/components/FilterPill";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

// Format eCourts last-synced timestamp → compact IST datetime string
function fmtEcourtsTs(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: true,
        });
    } catch { return null; }
}

const EMPTY_OPP = { name: "", role: "", advocate: "" };

// v2.19 — Refresh all disposed cases. Disposed cases are excluded from the
// hearing-anchored automatic sweeps (they'd waste API calls). This button
// lets the tenant re-check them on demand — for example, if a case they
// thought was disposed got re-listed.
function RefreshDisposedButton({ onDone }) {
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const r = await api.post("/cases/refresh-disposed-bulk", {});
            const d = r.data || {};
            toast.success(`Refreshed ${d.refreshed || 0} disposed cases · ${d.changed_dates || 0} got new dates`);
            setConfirmOpen(false);
            onDone?.();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <>
            <GhostButton onClick={() => setConfirmOpen(true)}
                data-testid="cases-refresh-disposed-btn">
                <RotateCw className="w-3.5 h-3.5 mr-1"/> Refresh disposed
            </GhostButton>
            {confirmOpen && (
                <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Refresh disposed cases?" testId="refresh-disposed-modal">
                    <div className="p-4 space-y-3">
                        <p className="text-sm text-muted-foreground">
                            This will run <code className="text-primary">CASE_REFRESH</code> for every case in your firm marked as disposed (capped at 200). Each call debits your eCourts wallet (~₹3.00/case billed). Cases that have been re-listed will automatically un-disposed.
                        </p>
                        <div className="flex gap-2 justify-end">
                            <GhostButton onClick={() => setConfirmOpen(false)}>Cancel</GhostButton>
                            <GoldButton onClick={run} disabled={busy} data-testid="refresh-disposed-confirm">
                                {busy ? "Refreshing…" : "Refresh all disposed"}
                            </GoldButton>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

export default function Cases() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [configs, setConfigs] = useState({});
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);
    const [form, setForm] = useState({
        title: "", client_id: "", court_case_number: "", court: "",
        category: "", type: "", stage: "Filed", priority: "medium", description: ""
    });
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const statusFilter      = params.get("status");        // active | disposed | …
    const nextHearingFilter = params.get("next_hearing");  // today | tomorrow | upcoming
    const hearingDateFilter = params.get("hearing_date");  // YYYY-MM-DD from Calendar

    // IST date helpers — consistent with backend _ist_today()
    const _istDate = (offsetDays = 0) => {
        const now = new Date();
        const ist = new Date(now.getTime() + (5.5 * 60 * 60 * 1000) + (offsetDays * 86400000));
        return ist.toISOString().slice(0, 10);
    };

    const visibleItems = useMemo(() => {
        let list = items;
        // next_hearing filter — show cases with next_hearing_date_ecourts = today/tomorrow/upcoming 7d
        if (nextHearingFilter === "today") {
            const today = _istDate(0);
            list = list.filter(c => c.next_hearing_date_ecourts?.slice(0, 10) === today);
        } else if (nextHearingFilter === "tomorrow") {
            const tomorrow = _istDate(1);
            list = list.filter(c => c.next_hearing_date_ecourts?.slice(0, 10) === tomorrow);
        } else if (nextHearingFilter === "upcoming") {
            const today  = _istDate(0);
            const in7d   = _istDate(7);
            list = list.filter(c => {
                const d = c.next_hearing_date_ecourts?.slice(0, 10);
                return d && d >= today && d <= in7d;
            });
        }
        // Calendar date filter — from clicking a date on CalendarPage
        if (hearingDateFilter) {
            list = list.filter(c => c.next_hearing_date_ecourts?.slice(0, 10) === hearingDateFilter);
        }
        // status filter
        if (!statusFilter) return list;
        const INACTIVE = new Set([
            "closed", "won", "lost",
            "disposed", "dismissed", "withdrawn", "settled", "abandoned",
        ]);
        if (statusFilter === "active") {
            return list.filter(c => {
                const s = String(c.status || "").toLowerCase().trim();
                if (INACTIVE.has(s)) return false;
                if (c.ecourts_disposed === true) return false;
                return true;
            });
        }
        return list.filter(c => c.status === statusFilter);
    }, [items, statusFilter, nextHearingFilter, hearingDateFilter]);

    const _fmtFilterDate = (d) => {
        if (!d) return d;
        try {
            return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kolkata" });
        } catch { return d; }
    };

    const filterLabel = hearingDateFilter
        ? `Hearings on ${_fmtFilterDate(hearingDateFilter)}`
        : nextHearingFilter === "today"     ? "Hearings today"
        : nextHearingFilter === "tomorrow"  ? "Hearings tomorrow"
        : nextHearingFilter === "upcoming"  ? "Upcoming hearings (next 7 days)"
        : statusFilter === "active"        ? "Active litigations only"
        : statusFilter                     ? `Status: ${statusFilter}`
        : null;

    const tableCtrl = useTableControls(visibleItems, {
        storageKey: "litigations",
        accessors: {
            internal_number: (c) => caseDisplayNumber(c),
            next_hearing_date_ecourts: (c) => c.next_hearing_date_ecourts || "",
        },
    });

    const load = async () => {
        try {
            const [cs, cl, cats, types, stages] = await Promise.all([
                api.get("/cases"),
                api.get("/clients"),
                api.get("/config/case_categories"),
                api.get("/config/case_types"),
                api.get("/config/case_stages"),
            ]);
            setItems(cs.data); setClients(cl.data);
            setConfigs({ categories: cats.data, types: types.data, stages: stages.data });
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            const payload = {
                ...form,
                opposite_parties: (form.opposite_parties || []).filter(o => o.name?.trim()),
                // Include history data from the CNR lookup (backend persists these free)
                initial_hearings: form.initial_hearings || [],
                initial_orders:   form.initial_orders   || [],
            };
            // Remove frontend-only fields that must not be sent to the API
            delete payload._ecourts_parties;
            await api.post("/cases", payload);
            toast.success(form.record_type === "legal_opinion" ? "Legal opinion created" : "Litigation created");
            setOpen(false); load();
            setForm({ record_type: "case", title: "", client_id: "", court_case_number: "", court: "", bench: "", hearing_type: "", category: "", type: "", stage: "Filed", priority: "medium", description: "", opposite_parties: [{ ...EMPTY_OPP }] });
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const setOpp = (idx, key, val) => {
        const next = [...(form.opposite_parties || [])];
        next[idx] = { ...next[idx], [key]: val };
        setForm({ ...form, opposite_parties: next });
    };
    const addOpp = () => setForm({ ...form, opposite_parties: [...(form.opposite_parties || []), { ...EMPTY_OPP }] });
    const removeOpp = (idx) => {
        const next = [...(form.opposite_parties || [])];
        next.splice(idx, 1);
        setForm({ ...form, opposite_parties: next.length ? next : [{ ...EMPTY_OPP }] });
    };

    const openCase = async (id) => {
        try { const { data } = await api.get(`/cases/${id}`); setActive(data); }
        catch (e) { toast.error(apiError(e)); }
    };

    const summarize = async () => {
        if (!active) return;
        setAiBusy(true);
        try {
            const text = `Case ${caseDisplayNumber(active)} (${active.internal_number}): ${active.title}
Court: ${active.court || "-"}
Court Number: ${active.court_case_number || "-"}
Category: ${active.category || "-"} | Type: ${active.type || "-"} | Stage: ${active.stage || "-"}
Client: ${active.client?.name || "-"}
Description: ${active.description || "-"}
Hearings: ${active.hearings?.length || 0}, Tasks: ${active.tasks?.length || 0}, Documents: ${active.documents?.length || 0}.`;
            const { data } = await api.post("/ai/summary", { text, kind: "case" });
            setActive({ ...active, ai_summary: data.summary });
        } catch (e) { toast.error(apiError(e)); } finally { setAiBusy(false); }
    };

    const updateStage = async (stage) => {
        if (!active) return;
        const reason = window.prompt("Reason for changing stage (mandatory · logged to audit):");
        if (!reason || reason.trim().length < 3) {
            toast.error("Stage change requires a reason of at least 3 characters");
            return;
        }
        try {
            const { data } = await api.patch(`/cases/${active.id}`, { stage }, { headers: { "X-Change-Reason": reason.trim() } });
            setActive({ ...active, stage: data.stage });
            load();
            toast.success("Stage updated · logged to audit trail");
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader
                title="Litigations"
                subtitle="Every matter, from filing to disposal — across courts and benches."
                action={
                    <div className="flex items-center gap-2">
                        <RefreshDisposedButton onDone={() => window.location.reload()}/>
                        <GoldButton onClick={() => setOpen(true)} data-testid="cases-add-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>New litigation</GoldButton>
                    </div>
                }
            />

            {filterLabel && (
                <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="cases-filter-banner">
                    <FilterPill label={filterLabel} testId={`cases-pill-${statusFilter || hearingDateFilter}`}/>
                    <span className="text-xs text-muted-foreground">{visibleItems.length} litigation{visibleItems.length === 1 ? "" : "s"}</span>
                    {(hearingDateFilter || nextHearingFilter) && (
                        <button onClick={() => setParams({})} data-testid="cases-clear-date-filter"
                            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
                            Clear filter
                        </button>
                    )}
                </div>
            )}

            {visibleItems.length === 0 ? (
                <EmptyState title={filterLabel ? "No matching litigations" : "No litigations yet"} subtitle={filterLabel ? "Try clearing the filter to see all litigations." : "Create your first litigation to start building the diary."} action={<GoldButton onClick={() => setOpen(true)} data-testid="cases-empty-add">Create litigation</GoldButton>}/>
            ) : (
                <>
                <FilterChip ctrl={tableCtrl} className="mb-3"/>
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[760px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <SortableTh field="internal_number" ctrl={tableCtrl}>No.</SortableTh>
                                <SortableTh field="title" ctrl={tableCtrl}>Title</SortableTh>
                                <SortableTh field="client_name" ctrl={tableCtrl}>Client</SortableTh>
                                <SortableTh field="court" ctrl={tableCtrl}>Court</SortableTh>
                                <SortableTh field="stage" ctrl={tableCtrl}>Stage</SortableTh>
                                <SortableTh field="next_hearing_date_ecourts" ctrl={tableCtrl}>Next Hearing</SortableTh>
                                <SortableTh field="status" ctrl={tableCtrl}>Status</SortableTh>
                                <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">eCourts Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableCtrl.rows.map(c => (
                                <tr key={c.id} onClick={() => navigate(`/app/cases/${c.id}`)} className="border-b border-border hover:bg-muted/50 cursor-pointer" data-testid={`case-row-${c.id}`}>
                                    <td className="px-4 py-3 font-mono text-xs">
                                        <div className="text-primary" data-testid={`case-display-no-${c.id}`}>{caseDisplayNumber(c)}</div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">{c.internal_number}</div>
                                        {c.court_case_number && c.court_case_number !== c.original_suit_number && c.court_case_number !== caseDisplayNumber(c) && (
                                            <div className="text-[10px] text-[#5A5A5A] mt-0.5">CIS {c.court_case_number}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">{c.title}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.client_name || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{c.court || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground">
                                        <span>{c.stage || "—"}</span>
                                        {c.ecourts_source && <div className="text-[10px] text-primary/70 mt-0.5">eCourts</div>}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground" data-testid={`case-next-hearing-${c.id}`}>
                                        {c.next_hearing_date_ecourts ? (
                                            <div>
                                                <div>{fmtDate(c.next_hearing_date_ecourts)}</div>
                                                <div className="text-[10px] text-primary/70 mt-0.5">eCourts</div>
                                            </div>
                                        ) : (
                                            <span className="text-[#5A5A5A]">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3"><StatusBadge status={c.status}/>
                                        {c.watch_tier === "hot" && (
                                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-orange-500/15 border border-orange-400/40 text-orange-300" data-testid={`case-tier-${c.id}`}>HOT</span>
                                        )}
                                        {c.possibly_closed && (
                                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-amber-500/15 border border-amber-400/40 text-amber-300" data-testid={`case-closed-${c.id}`}>? Closed</span>
                                        )}
                                        {c.strategy2_pending_cycle && (
                                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-blue-500/15 border border-blue-400/40 text-blue-300" title="HOT refresh skipped today (cost cap). Pending next cycle." data-testid={`case-pending-${c.id}`}>Pending</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3" data-testid={`case-ecourts-updated-${c.id}`}>
                                        {(c.ecourts_last_synced_at || c.ecourts_last_refreshed_at) ? (
                                            <div className="flex items-start gap-1">
                                                <Clock className="w-3 h-3 text-primary/60 mt-0.5 flex-shrink-0"/>
                                                <div>
                                                    <div className="text-[11px] text-foreground font-medium leading-tight">
                                                        {fmtEcourtsTs(c.ecourts_last_synced_at || c.ecourts_last_refreshed_at)}
                                                    </div>
                                                    {c.ecourts_last_synced_at && (
                                                        <div className="text-[10px] text-primary/70 mt-0.5">Synced</div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-[11px] text-muted-foreground">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            {/* Create modal */}
            <Modal open={open} onClose={() => setOpen(false)} title="New litigation" size="lg" testId="case-modal">
                <form onSubmit={submit} className="space-y-4">
                    <div className="flex items-center gap-4 mb-2">
                        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="case-record-type-case">
                            <input type="radio" className="w-4 h-4 accent-primary" checked={form.record_type === "case"} onChange={() => setForm({...form, record_type: "case"})}/>
                            <span>Litigation</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="case-record-type-opinion">
                            <input type="radio" className="w-4 h-4 accent-primary" checked={form.record_type === "legal_opinion"} onChange={() => setForm({...form, record_type: "legal_opinion"})}/>
                            <span>Legal Opinion</span>
                        </label>
                        <span className="text-[10px] text-muted-foreground ml-auto">{form.record_type === "legal_opinion" ? "An LO-#### reference will be auto-generated" : "A CS-#### internal number will be auto-generated"}</span>
                    </div>
                    {form.record_type === "case" && <CnrAutofill form={form} setForm={setForm}/>}
                    <Field label="Title"><Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="case-title-input"/></Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Client">
                            <Select required value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} data-testid="case-client-select"
                                options={[{value: "", label: "— select —"}, ...clients.map(c => ({value: c.id, label: `${c.code} — ${c.name}`}))]}/>
                        </Field>
                        <Field label="Court case number"><Input value={form.court_case_number} onChange={e => setForm({...form, court_case_number: e.target.value})}/></Field>
                        <Field label="Court"><Input value={form.court} onChange={e => setForm({...form, court: e.target.value})} placeholder="e.g. Madras High Court"/></Field>
                        <Field label="Bench"><Input value={form.bench} onChange={e => setForm({...form, bench: e.target.value})} placeholder="e.g. Principal Bench"/></Field>
                        <Field label="Hearing type"><Input value={form.hearing_type} onChange={e => setForm({...form, hearing_type: e.target.value})} placeholder="e.g. Final hearing"/></Field>
                        <Field label="Category">
                            <Select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                                options={_mergeOption(form.category, [{value: "", label: "—"}, ...(configs.categories || []).map(c => c.name)])}/>
                        </Field>
                        <Field label="Type">
                            <Select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                                options={_mergeOption(form.type, [{value: "", label: "—"}, ...(configs.types || []).map(c => c.name)])}/>
                        </Field>
                        <Field label="Stage">
                            <Select value={form.stage} onChange={e => setForm({...form, stage: e.target.value})}
                                options={_mergeOption(form.stage, (configs.stages || []).map(c => c.name))}/>
                        </Field>
                        <Field label="Priority">
                            <Select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} options={["low", "medium", "high", "urgent"]}/>
                        </Field>
                    </div>
                    <Field label="Description"><Textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})}/></Field>

                    {form.record_type === "case" && (
                        <div className="bg-background border border-border p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="overline">Opposite parties</div>
                                <div className="flex items-center gap-3">
                                    <EcourtsPartyPicker form={form} setForm={setForm} />
                                    <button type="button" onClick={addOpp} data-testid="opposite-add-btn" className="text-[10px] uppercase tracking-[0.2em] text-primary hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" strokeWidth={2}/>Add another</button>
                                </div>
                            </div>
                            <div className="space-y-4">
                                {(form.opposite_parties || []).map((op, idx) => (
                                    <div key={`opp-${idx}-${op.name || ""}`} className="border border-muted p-3" data-testid={`opposite-row-${idx}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Party {idx + 1}</div>
                                            {(form.opposite_parties || []).length > 1 && (
                                                <button type="button" onClick={() => removeOpp(idx)} data-testid={`opposite-remove-${idx}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/></button>
                                            )}
                                        </div>
                                        <div className="grid sm:grid-cols-2 gap-3">
                                            <Field label="Name">
                                                <PartyNameInput
                                                    value={op.name}
                                                    onChange={v => setOpp(idx, "name", v)}
                                                    suggestions={form._ecourts_parties || []}
                                                    onPick={(party) => {
                                                        const next = [...(form.opposite_parties || [])];
                                                        next[idx] = {
                                                            ...next[idx],
                                                            name: party.name,
                                                            advocate_name: party.advocate_name || next[idx].advocate_name,
                                                            _source: "ecourts",
                                                        };
                                                        setForm({ ...form, opposite_parties: next });
                                                    }}
                                                    testId={`opp-name-${idx}`}
                                                />
                                            </Field>
                                            <Field label="Contact"><Input value={op.contact} onChange={e => setOpp(idx, "contact", e.target.value)}/></Field>
                                            <Field label="Email"><Input type="email" value={op.email} onChange={e => setOpp(idx, "email", e.target.value)}/></Field>
                                            <Field label="Address"><Input value={op.address} onChange={e => setOpp(idx, "address", e.target.value)}/></Field>
                                            <Field label="Advocate (optional)"><Input value={op.advocate_name} onChange={e => setOpp(idx, "advocate_name", e.target.value)}/></Field>
                                            <Field label="Advocate contact (optional)"><Input value={op.advocate_contact} onChange={e => setOpp(idx, "advocate_contact", e.target.value)}/></Field>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="pt-2 flex justify-end"><GoldButton disabled={saving} data-testid="case-save-btn">{saving ? "Creating…" : (form.record_type === "legal_opinion" ? "Create legal opinion" : "Create litigation")}</GoldButton></div>
                </form>
            </Modal>

            {/* Detail modal */}
            <Modal open={!!active} onClose={() => setActive(null)} title={active?.title || ""} size="xl" testId="case-detail-modal">
                {active && (
                    <div className="space-y-8">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="font-mono text-xs text-primary mb-1" data-testid="active-case-display-no">{caseDisplayNumber(active)} <span className="text-muted-foreground">· {active.internal_number}</span></div>
                                <div className="text-sm text-muted-foreground">{active.court || "—"} · {active.category || "—"} · {active.type || "—"}</div>
                                <div className="mt-2"><StatusBadge status={active.status}/></div>
                            </div>
                            <div className="flex gap-2">
                                <Select value={active.stage} onChange={e => updateStage(e.target.value)} options={(configs.stages || []).map(c => c.name)} className="w-48" data-testid="case-stage-select"/>
                                <GhostButton onClick={summarize} disabled={aiBusy} data-testid="case-ai-summary-btn"><Sparkles className="w-4 h-4 inline mr-2" strokeWidth={1.5}/>{aiBusy ? "Thinking…" : "AI summary"}</GhostButton>
                            </div>
                        </div>
                        {active.client && (
                            <div className="bg-background border border-border p-4">
                                <div className="overline mb-2">Client</div>
                                <div className="text-sm">{active.client.name} <span className="text-muted-foreground">· {active.client.code}</span></div>
                                <div className="text-xs text-muted-foreground">{active.client.mobile || ""} {active.client.email || ""}</div>
                            </div>
                        )}
                        {active.ai_summary && (
                            <div className="bg-muted border border-primary/40 p-4">
                                <div className="overline mb-3">AI summary</div>
                                <div className="text-sm whitespace-pre-wrap leading-relaxed">{active.ai_summary}</div>
                            </div>
                        )}
                        <div>
                            <div className="overline mb-3">Hearings</div>
                            {active.hearings?.length ? (
                                <div className="divide-y divide-border border border-border">
                                    {active.hearings.map(h => (
                                        <div key={h.id} className="px-4 py-3 flex items-center justify-between text-sm">
                                            <div>
                                                <div className="font-mono text-xs text-muted-foreground">{fmtDateTime(h.scheduled_at)}</div>
                                                <div>{h.purpose || "Hearing"}</div>
                                            </div>
                                            <StatusBadge status={h.status}/>
                                        </div>
                                    ))}
                                </div>
                            ) : <div className="text-sm text-muted-foreground">No hearings.</div>}
                        </div>
                        <div>
                            <div className="overline mb-3">Description</div>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{active.description || "—"}</p>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
}

/**
 * Ensures a dropdown can render any externally-supplied value (e.g. from
 * eCourts) even when the firm's master data doesn't have it yet. Pushes the
 * value as an extra option if missing.
 */
function _mergeOption(value, opts) {
    if (!value) return opts;
    const has = opts.some(o => (typeof o === "string" ? o === value : o.value === value));
    if (has) return opts;
    return [...opts, { value, label: `${value} (from eCourts)` }];
}

/**
 * PartyNameInput — text input with a dropdown of eCourts-supplied party names.
 * The user can either type manually OR click a suggestion to autofill the name +
 * advocate. Once typed, behaves like a normal text input.
 */
function PartyNameInput({ value, onChange, suggestions = [], onPick, testId }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);
    const hasSugs = suggestions && suggestions.length > 0;
    return (
        <div className="relative" ref={ref}>
            <div className="flex gap-1">
                <input value={value || ""} onChange={e => onChange(e.target.value)}
                    data-testid={testId}
                    className="flex-1 bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                {hasSugs && (
                    <button type="button" onClick={() => setOpen(s => !s)}
                        data-testid={`${testId}-pick`}
                        title="Pick from eCourts"
                        className="px-2 py-2 border border-border text-primary hover:border-primary">
                        <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5}/>
                    </button>
                )}
            </div>
            {open && hasSugs && (
                <div className="absolute z-30 mt-1 right-0 left-0 bg-background border border-border shadow-2xl max-h-56 overflow-auto"
                     data-testid={`${testId}-popup`}>
                    <div className="px-3 py-1.5 text-[10px] text-muted-foreground uppercase tracking-[0.2em] border-b border-border">
                        From eCourts
                    </div>
                    {suggestions.map((p, i) => (
                        <button key={`${p.name}-${i}`} type="button"
                            onClick={() => { onPick?.(p); setOpen(false); }}
                            data-testid={`${testId}-opt-${i}`}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-muted border-b border-muted last:border-b-0">
                            <div className="text-foreground">{p.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                                {p.side}{p.advocate_name ? ` · Adv: ${p.advocate_name}` : ""}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * EcourtsPartyPicker — small "+ From eCourts (N)" button next to Add Another.
 * Drops down a list of party names (respondents + petitioners) from the last
 * CNR fetch. Clicking one APPENDS a new opposite_parties row with name + advocate.
 */
function EcourtsPartyPicker({ form, setForm }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);
    const sugs = form._ecourts_parties || [];
    if (sugs.length === 0) return null;

    const add = (p) => {
        const exists = (form.opposite_parties || []).some(o => o.name?.trim().toLowerCase() === p.name.trim().toLowerCase());
        if (exists) {
            toast.info(`${p.name} is already added`);
            setOpen(false);
            return;
        }
        const current = form.opposite_parties || [];
        // Replace the first empty row, or append a new one
        const firstEmptyIdx = current.findIndex(o => !o.name?.trim());
        const newRow = { name: p.name, contact: "", email: "", address: "",
                         advocate_name: p.advocate_name || "", advocate_contact: "", _source: "ecourts" };
        let next;
        if (firstEmptyIdx >= 0) {
            next = [...current];
            next[firstEmptyIdx] = newRow;
        } else {
            next = [...current, newRow];
        }
        setForm({ ...form, opposite_parties: next });
        setOpen(false);
        toast.success(`Added ${p.name} from eCourts`);
    };

    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOpen(s => !s)}
                data-testid="opposite-pick-ecourts-btn"
                className="text-[10px] uppercase tracking-[0.2em] text-primary hover:underline inline-flex items-center gap-1">
                <Users className="w-3 h-3" strokeWidth={2}/>From eCourts ({sugs.length})
                <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2}/>
            </button>
            {open && (
                <div className="absolute z-30 right-0 mt-1 bg-background border border-border shadow-2xl min-w-[260px] max-h-72 overflow-auto"
                     data-testid="opposite-pick-ecourts-popup">
                    {sugs.map((p, i) => (
                        <button key={`${p.name}-${i}`} type="button" onClick={() => add(p)}
                            data-testid={`opposite-pick-opt-${i}`}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-muted border-b border-muted last:border-b-0">
                            <div className="text-foreground">{p.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                                {p.side}{p.advocate_name ? ` · Adv: ${p.advocate_name}` : ""}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * CnrAutofill — paste a CNR, hit "Fetch", and the litigation form auto-fills with
 * data pulled from eCourts (parties, judges, dates, status, court).
 *
 * The wallet is debited via /api/ecourts/case/{cnr} at the platform's configured
 * rate. We pre-populate the most useful fields and let the user adjust before save.
 */
function CnrAutofill({ form, setForm }) {
    const [cnr, setCnr] = useState(form.cnr || "");
    const [busy, setBusy] = useState(false);
    const [synced, setSynced] = useState(!!form.ecourts_last_synced_at);
    const [lastFilled, setLastFilled] = useState(null);   // {fields, raw_keys_returned}
    const [showDetails, setShowDetails] = useState(false);

    const fetchCnr = async () => {
        const clean = (cnr || "").trim().toUpperCase();
        if (!/^[A-Z]{4}\d{12}$/.test(clean)) {
            toast.error("CNR must be 16 chars: 4 letters + 12 digits (e.g. DLHC010001232024)");
            return;
        }
        setBusy(true);
        try {
            const r = await api.get(`/ecourts/case/${clean}`);
            const { fields, hearings, orders, cost } = r.data;
            // Merge: only overwrite fields that are empty in the form, EXCEPT for
            // eCourts-source fields (cnr, judges, dates, etc.) which always win.
            // Every field that originates from the eCourts API response.
            // These ALWAYS override whatever is in the form — even manual edits —
            // because eCourts is the single source of truth for all of them.
            const ecOwned = [
                // Core identifiers
                "cnr", "court_case_number", "original_suit_number",
                "registration_number", "registration_date",
                // Court / bench / procedure
                "court", "bench", "hearing_type", "category", "type", "stage",
                // Parties
                "judges", "petitioners", "respondents",
                "petitioner_advocates", "respondent_advocates",
                // Dates
                "filing_date", "first_hearing_date", "next_hearing_date_ecourts",
                "decision_date",
                // Disposal / status
                "disposal_type", "contested_status", "status", "ecourts_disposed",
                // eCourts metadata
                "case_category_facet_path", "ecourts_case_type_raw",
                "ecourts_state_code", "ecourts_district", "ecourts_court_name",
                "ecourts_case_type", "ecourts_court_code",
                "ecourts_hearing_count", "ecourts_order_count",
                "ecourts_judgment_count", "ecourts_last_synced_at", "ecourts_source",
            ];
            const merged = { ...form };
            for (const [k, v] of Object.entries(fields)) {
                if (k === "_suggested_title") {
                    if (!merged.title?.trim()) merged.title = v;
                    continue;
                }
                if (ecOwned.includes(k) || !merged[k]) merged[k] = v;
            }
            // Auto-fill opposite_parties from eCourts respondents (if user hasn't
            // started entering them manually). Each respondent → one row; we
            // also auto-attach the respondent advocates by index when available.
            const respondents = fields.respondents || [];
            const respAdvs = fields.respondent_advocates || [];
            const noneEnteredYet = (merged.opposite_parties || [])
                .every(o => !o.name?.trim() && !o.contact?.trim() && !o.email?.trim());
            if (respondents.length > 0 && noneEnteredYet) {
                merged.opposite_parties = respondents.map((name, i) => ({
                    name,
                    contact: "",
                    email: "",
                    address: "",
                    advocate_name: respAdvs[i] || (respAdvs.length === 1 ? respAdvs[0] : ""),
                    advocate_contact: "",
                    _source: "ecourts",
                }));
            }
            // Stash all eCourts parties so the "Pick from eCourts" dropdown
            // can re-add them later if the user deletes a row.
            merged._ecourts_parties = [
                ...(fields.respondents || []).map((n, i) => ({
                    name: n, side: "respondent",
                    advocate_name: (fields.respondent_advocates || [])[i] || "",
                })),
                ...(fields.petitioners || []).map((n, i) => ({
                    name: n, side: "petitioner",
                    advocate_name: (fields.petitioner_advocates || [])[i] || "",
                })),
            ];
            setForm(merged);
            setSynced(true);
            setLastFilled({ fields, raw_keys_returned: r.data.raw_keys_returned || [] });
            // Store hearings + orders from this lookup so they are persisted
            // into case_hearings / case_orders when the case is saved — no
            // second eCourts call or charge will be needed for Court History.
            setForm(prev => ({
                ...prev,
                initial_hearings: hearings || [],
                initial_orders:   orders   || [],
            }));
            toast.success(`eCourts data fetched · ₹${cost.billed_inr.toFixed(2)} billed to wallet`);
        } catch (e) {
            toast.error(apiError(e));
        } finally { setBusy(false); }
    };

    return (
        <div className="bg-background border border-primary/30 p-4 mb-2" data-testid="cnr-autofill-block">
            <div className="flex items-center gap-2 mb-2">
                <Scale className="w-4 h-4 text-primary" strokeWidth={1.5}/>
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary">Auto-fill from eCourts</div>
                {synced && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-emerald-400">
                        <CheckCircle2 className="w-3 h-3"/> Synced
                    </span>
                )}
            </div>
            <div className="text-[10px] text-muted-foreground mb-3">
                Paste a 16-char CNR (e.g. <span className="font-mono">DLHC010001232024</span>) — we'll pull parties, judges, hearings &amp; orders straight from eCourts.gov.in. Costs are debited from your firm's AI Wallet.
            </div>
            <div className="flex gap-2">
                <input value={cnr} onChange={e => setCnr(e.target.value.toUpperCase())}
                    placeholder="DLHC010001232024"
                    maxLength={16}
                    data-testid="cnr-input"
                    className="flex-1 bg-muted border border-border px-3 py-2 text-sm font-mono uppercase focus:border-primary outline-none"/>
                <button type="button" onClick={fetchCnr} disabled={busy || !cnr.trim()}
                    data-testid="cnr-fetch-btn"
                    className="px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary disabled:opacity-40 flex items-center gap-1.5">
                    {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Fetching…</> : <>Fetch &amp; fill</>}
                </button>
            </div>
            {lastFilled && (
                <div className="mt-3 text-[10px]">
                    <button type="button" onClick={() => setShowDetails(s => !s)}
                        data-testid="cnr-details-toggle"
                        className="text-muted-foreground underline hover:text-primary">
                        {showDetails ? "Hide" : "Show"} what was filled ({Object.keys(lastFilled.fields).length} fields)
                    </button>
                    {showDetails && (
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 font-mono text-[10px] text-muted-foreground max-h-60 overflow-auto bg-muted p-3">
                            {Object.entries(lastFilled.fields).map(([k, v]) => (
                                <div key={k}>
                                    <span className="text-primary">{k}:</span>{" "}
                                    <span>{Array.isArray(v) ? v.join(", ") || "—" : String(v)}</span>
                                </div>
                            ))}
                            <div className="col-span-full mt-2 pt-2 border-t border-border text-muted-foreground">
                                <span className="text-primary">Raw keys returned by eCourts:</span>{" "}
                                {lastFilled.raw_keys_returned.join(", ") || "—"}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}